import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";
import { maintenanceData } from "@/mocks/designServiceData";

export default function MaintenanceSupportPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/services" },
          { label: "Maintenance & Support" },
        ]}
      />

      <PageHero
        badge={maintenanceData.hero.badge}
        headline={maintenanceData.hero.headline}
        subtext={maintenanceData.hero.subtext}
        primaryCTA={maintenanceData.hero.primaryCTA}
        secondaryCTA={maintenanceData.hero.secondaryCTA}
      />

      {/* Capabilities */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Maintenance & Support Services
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {maintenanceData.capabilities.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-background-100 relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-30" />
        <div className="container-astro relative z-10">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Why Ongoing Support Matters
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {maintenanceData.benefits.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 100}>
                <div className="group p-7 md:p-8 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">
                Our Support Process
              </h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute top-[52px] left-[7%] right-[7%] h-px bg-gradient-to-r from-accent-500/20 via-accent-500/40 to-accent-500/20" />
              <div className="grid grid-cols-6 gap-4">
                {maintenanceData.process.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 120}>
                    <div className="text-center group">
                      <div className="relative inline-flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading mb-5 mx-auto group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300 z-10">
                        {step.number}
                      </div>
                      <h3 className="text-sm font-bold text-foreground-950 mb-2 font-heading">
                        {step.title}
                      </h3>
                      <p className="text-xs text-foreground-500 leading-relaxed max-w-[140px] mx-auto">
                        {step.desc}
                      </p>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:hidden">
            <div className="relative max-w-md mx-auto">
              <div className="absolute left-[25px] top-0 bottom-0 w-px bg-gradient-to-b from-accent-500/30 via-accent-500/50 to-accent-500/30" />
              <div className="flex flex-col gap-8">
                {maintenanceData.process.map((step, i) => (
                  <AnimatedSection key={step.number} delay={i * 100}>
                    <div className="flex items-start gap-5 group">
                      <div className="relative z-10 flex items-center justify-center w-[52px] h-[52px] rounded-full bg-background-100 border-2 border-accent-500/30 text-accent-600 font-extrabold text-sm font-heading shrink-0 group-hover:bg-accent-500 group-hover:text-background-50 group-hover:border-accent-500 transition-all duration-300">
                        {step.number}
                      </div>
                      <div className="pt-2.5">
                        <h3 className="text-base font-bold text-foreground-950 mb-1.5 font-heading">
                          {step.title}
                        </h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection heading="Maintenance & Support FAQ" items={maintenanceData.faq} />

      <PageCTA
        heading={maintenanceData.cta.heading}
        text={maintenanceData.cta.text}
        primaryCTA={maintenanceData.cta.primaryCTA}
        secondaryCTA={maintenanceData.cta.secondaryCTA}
      />
    </PageLayout>
  );
}